import dayjs from 'dayjs'
import { Formik } from 'formik'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { StringParam, useQueryParams } from 'use-query-params'

import { useDashboardFeatureFlags } from '@components'
import { LeavingGuard } from '@components/utils/LeavingGuard'
import {
  ConfirmModal,
  HeadContent,
  SaveShortcutListener,
  useModalContext,
} from '@fourthwall/components'
import { ConfirmBar } from '@modules/Dashboard/modules/ConfirmBar'
import { TimezonedDateString } from '@modules/Timezone/components/TimezonedDateString'
import { routing } from '@utils/routing'

import { ScheduleInput } from '../../components/ScheduleInput'
import { useCreateImageResources } from '../../hooks/useCreateImageResources'
import type { PostType } from '../../types'
import { useTiers } from '../Perks/hooks/useTiers'
import { PostViewSkeleton } from './PostSkeleton'
import { PostCreateLayout } from './components/PostCreateLayout'
import { getValidationSchema } from './consts'
import { usePostCreate } from './hooks/usePostCreate'
import { AudioFields } from './modules/AudioFields'
import { CommonFields } from './modules/CommonFields'
import { ImagesFields } from './modules/ImagesFields'
import { LiveStreamCreateFields } from './modules/LiveStreamCreateFields'
import { PollFields } from './modules/PollFields'
import { SeriesFields } from './modules/SeriesFields'
import { VideoFields } from './modules/VideoFields'
import type { FormValues } from './types'
import { FormFields } from './types'
import { getSelectedTierIds } from './utils/getSelectedTierIds'

export const PostCreate = () => {
  const navigate = useNavigate()
  const modal = useModalContext()
  const [queryParams] = useQueryParams({
    type: StringParam,
  })
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()

  const { tiers } = useTiers()
  const { createPost, mediaUploadProgress, isLoading } = usePostCreate()
  const { createImageResources, isLoading: createImageResourcesIsLoading } =
    useCreateImageResources()

  if (!tiers) {
    return <PostViewSkeleton />
  }

  const getTiers = getSelectedTierIds([], tiers)

  const initialValues: FormValues = {
    mode: 'create',
    postType: (queryParams.type as PostType) || 'Text',
    access: 'MembersOnly',
    tierIds: getTiers,
    title: '',
    description: '',
    tags: [],
    notifyMembers: true,
    images: [],
    videoType: 'upload',
    pollOptions: ['', ''],
    pollLength: {
      days: '3',
      hours: '',
      minutes: '',
    },
  }

  const getConfirmBarButtonLabel = (values: FormValues) => {
    if (!values.liveAt) {
      if (values.postType === 'LiveStream') return 'Go live now'

      return 'Publish'
    }

    if (dayjs(values.liveAt).isAfter(dayjs())) {
      return 'Schedule'
    }

    if (values.postType === 'LiveStream') return 'Go live now'

    return 'Publish'
  }

  const handleCreatePost = async (values: FormValues, resetForm: (values: any) => void) => {
    await createPost(values, () => {
      resetForm({ values })

      // "setTimeout" was used here to not trigger LeavingGuard
      setTimeout(() => {
        navigate(
          dayjs(values.liveAt).isAfter(dayjs()) && values.postType !== 'LiveStream'
            ? routing.memberships.posts.scheduled.all
            : routing.memberships.posts.self,
        )
      }, 0)
    })
  }

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      validationSchema={getValidationSchema({
        pollLenghValidation: true,
        publicVideo: dashboardFeatureFlags.membershipsPublicVideo.enabled,
      })}
      enableReinitialize
      onSubmit={async (values, { resetForm }) => {
        if (values.notifyMembers && dayjs(values.liveAt).isBefore(dayjs())) {
          return modal.open(ConfirmModal, {
            title: 'Are you sure you want to backdate this post?',
            text: (
              <p>
                Your post will display as if it was published on{' '}
                <TimezonedDateString
                  date={dayjs(values.liveAt)}
                  format="MMM DD, YYYY [at] hh:mm A"
                  withTimezoneAbbreviation={false}
                />
                . If you don’t want your members to be notified about this backdated post, turn off
                the “Notify members about this post
              </p>
            ),
            confirmLabel: 'Publish and notify members',
            onConfirm: () => handleCreatePost(values, resetForm),
          })
        }

        if (dayjs(values.liveAt).isBefore(dayjs())) {
          return modal.open(ConfirmModal, {
            title: 'Backdate this post?',
            text: (
              <p>
                Your post will be displayed as if it was published on{' '}
                <TimezonedDateString
                  date={dayjs(values.liveAt)}
                  format="MMM DD, YYYY [at] hh:mm A"
                  withTimezoneAbbreviation={false}
                />{' '}
                and may not be at the top of your page.
              </p>
            ),
            confirmLabel: 'Publish',
            onConfirm: () => handleCreatePost(values, resetForm),
          })
        }

        await handleCreatePost(values, resetForm)
      }}
    >
      {({ values, isValid, dirty, submitForm, setFieldValue, resetForm }) => {
        const handleImagesUpload = (files: File[]) => {
          createImageResources({
            files,
            onSuccess: (images) =>
              setFieldValue(FormFields.images, [
                ...values.images,
                ...images.map((image) => ({ image: image.resizedUrl, id: image.id })),
              ]),
          })
        }

        return (
          <>
            <HeadContent title="Create new post" />
            <PostCreateLayout disabled={isLoading}>
              {values.postType === 'Text' && <CommonFields disabled={isLoading} />}
              {values.postType === 'Image' && (
                <>
                  <ImagesFields
                    imagesFieldName={FormFields.images}
                    subsectionTitle="Images"
                    isUploading={isLoading || createImageResourcesIsLoading}
                    onImagesUpload={handleImagesUpload}
                  />
                  <CommonFields disabled={isLoading} />
                </>
              )}
              {values.postType === 'Video' && (
                <>
                  <VideoFields
                    type={values.videoType}
                    typeFieldName={FormFields.videoType}
                    video={values.video}
                    videoFieldName={FormFields.video}
                    thumbnail={values.videoThumbnail}
                    subtitles={values.videoSubtitles}
                    thumbnailFieldName={FormFields.videoThumbnail}
                    subtitlesFieldName={FormFields.videoSubtitles}
                    url={values.videoUrl}
                    urlFieldName={FormFields.videoUrl}
                    subsectionTitle="Video"
                    isUploading={isLoading}
                    uploadProgress={mediaUploadProgress}
                  />
                  <CommonFields disabled={isLoading}>
                    <SeriesFields disabled={isLoading} />
                  </CommonFields>
                </>
              )}
              {values.postType === 'Audio' && (
                <>
                  <AudioFields isUploading={isLoading} uploadProgress={mediaUploadProgress} />
                  <CommonFields disabled={isLoading} />
                </>
              )}
              {values.postType === 'Poll' && (
                <>
                  <CommonFields disabled={isLoading} />
                  <PollFields disabled={isLoading} />
                </>
              )}
              {values.postType === 'LiveStream' && (
                <>
                  <LiveStreamCreateFields disabled={isLoading} />
                  <CommonFields disabled={isLoading}>
                    <SeriesFields disabled={isLoading} />
                  </CommonFields>
                </>
              )}
            </PostCreateLayout>

            <SaveShortcutListener disabled={!dirty} onSave={submitForm} />
            <ConfirmBar
              isOpen={dirty}
              isLoading={isLoading || createImageResourcesIsLoading}
              confirmLabel={getConfirmBarButtonLabel(values)}
              message="Unsaved post"
              additionalElement={
                <ScheduleInput
                  date={values.liveAt}
                  onDateChange={(date) => {
                    setFieldValue(FormFields.liveAt, date)
                  }}
                  minDate={values.postType === 'LiveStream' ? new Date() : undefined}
                  label={
                    values.postType === 'LiveStream' ? 'Set livestream release date' : undefined
                  }
                  description={
                    values.postType === 'LiveStream'
                      ? 'Your post will be published now with a countdown, but the livestream will start at specific time.'
                      : undefined
                  }
                />
              }
              onConfirm={submitForm}
              onCancel={resetForm}
            />
            <LeavingGuard when={dirty} isValid={isValid} onSave={submitForm} />
          </>
        )
      }}
    </Formik>
  )
}
